import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import {
  BookingPostStatus,
  BookingPostSubmissionActivityType,
  BookingPostSubmissionsQuery,
  GetBookingQuery,
  useCreateBookingPostSubmissionActivityMutation,
  useUpdateBookingPostSubmissionMutation,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import useGqlClient from "../../../hooks/useGqlClient";
import { postTypeToAspectRatio } from "../../../utils/aspectRatio";
import { ActivityItem, MediaItem, MediaReviewDialog } from "../../MediaReview";
import { MediaReviewType } from "../../MediaReview/MediaReviewDialog";

interface Props {
  bookingPostSubmissions: BookingPostSubmissionsQuery["bookingPostSubmissions"]["bookingPostSubmissions"];
  booking: GetBookingQuery["booking"];
  isOpen: boolean;
  setIsOpen: (arg0: boolean) => void;
  activeSubmission?: BookingPostSubmissionsQuery["bookingPostSubmissions"]["bookingPostSubmissions"][0];
  onReview: () => void;
}

export const BookingPostSubmissionReview = (props: Props) => {
  const [activeSubmission, setActiveSubmission] = useState(
    () => props.activeSubmission ?? props.bookingPostSubmissions[0]
  );
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const { mutate: updateBookingPostSubmission } =
    useUpdateBookingPostSubmissionMutation(client, {
      onSuccess: () => {
        props.onReview();
      },
      onError: (error: any) => {
        console.error("Error approving booking post:", error);
      },
    });

  const { mutate: createBookingPostSubmissionActivity } =
    useCreateBookingPostSubmissionActivityMutation(client, {
      onSuccess: () => {
        // Invalidate queries to refresh the activity feed
        queryClient.invalidateQueries(["BookingPostSubmissions"], {
          exact: false,
        });
      },
      onError: (error: any) => {
        console.error("Error creating booking post activity:", error);
      },
    });

  const handleApprove = () => {
    if (!activeSubmission?.id) return;

    updateBookingPostSubmission({
      input: {
        id: activeSubmission.id,
        status: BookingPostStatus.BookingPostStatusApproved,
      },
    });
  };

  const handleAddComment = (comment: string, timestamp?: number) => {
    if (!activeSubmission?.id) return;

    createBookingPostSubmissionActivity(
      {
        input: {
          bookingPostSubmissionId: activeSubmission.id,
          comment,
          commentTimestampSeconds: timestamp,
          type: BookingPostSubmissionActivityType.Comment,
        },
      },
      {
        onSuccess: () => {
          // Refresh the query to show new activity
          queryClient.invalidateQueries(["BookingPostSubmissions"], {
            exact: false,
          });
        },
        onError: (error) => {
          console.error("Error creating booking post comment:", error);
        },
      }
    );
  };

  useEffect(() => {
    setActiveSubmission(
      props.activeSubmission ?? props.bookingPostSubmissions[0]
    );
  }, [props.activeSubmission, props.bookingPostSubmissions]);

  if (!activeSubmission) return null;

  // Transform booking post submission to MediaItem
  const media: MediaItem = {
    id: activeSubmission.id,
    mediaUrl: activeSubmission.mediaUrl,
    mediaType: activeSubmission.mediaType,
    thumbnailUrl: activeSubmission.thumbnailUrl || undefined,
    aspectRatio: postTypeToAspectRatio(activeSubmission.postType),
    mediaCaption: activeSubmission.mediaCaption,
  };

  // No transformation needed - ActivityItem interface now matches GraphQL schema
  const activities: ActivityItem[] = activeSubmission.activities || [];

  return (
    <MediaReviewDialog
      isOpen={props.isOpen}
      setIsOpen={props.setIsOpen}
      type={MediaReviewType.BookingPostSubmission}
      media={media}
      activities={activities}
      creator={props.booking.creator}
      platform={props.booking.platform}
      onApprove={handleApprove}
      onAddComment={handleAddComment}
      onActivityUpdate={() => {
        // Refresh the query to show new activity
        queryClient.invalidateQueries(queryKeys.bookingPostSubmissions, {
          exact: false,
        });
      }}
    />
  );
};
